import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthStore, canBrowseWithPendingTerms } from '../../stores/authStore';

/**
 * Route guard: redirects to /check/login if user is not authenticated.
 * On first mount, tries to fetch user info if token exists but user is null.
 */
export const AuthGuard: React.FC<{ children: React.ReactNode; allowTermsPending?: boolean }> = ({
  children,
  allowTermsPending = false,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, user, loading, fetchMe } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    // If we have a token but haven't loaded user yet, fetch it
    if (isAuthenticated && !user && !loading) {
      fetchMe();
    }
  }, [isAuthenticated, user, loading, fetchMe]);

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" tip={t('Loading...')} />
      </div>
    );
  }

  if (user?.terms_required && !(allowTermsPending && canBrowseWithPendingTerms(user))) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, termsRequired: true }}
        replace
      />
    );
  }

  return <>{children}</>;
};

/**
 * Route guard: requires admin or operator role.
 */
export const AdminGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, user, loading, fetchMe } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !user && !loading) {
      fetchMe();
    }
  }, [isAuthenticated, user, loading, fetchMe]);

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" tip={t('Loading...')} />
      </div>
    );
  }

  if (user && user.role !== 'admin' && user.role !== 'operator' && user.role !== 'dev') {
    return <Navigate to="/" replace />;
  }

  if (user?.terms_required) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, termsRequired: true }}
        replace
      />
    );
  }

  return <>{children}</>;
};

/**
 * Route guard: requires admin or dev role.
 */
export const AdminOnlyGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user, loading, fetchMe } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !user && !loading) {
      fetchMe();
    }
  }, [isAuthenticated, user, loading, fetchMe]);

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (user && user.role !== 'admin' && user.role !== 'dev') {
    return <Navigate to="/admin" replace />;
  }

  if (user?.terms_required) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, termsRequired: true }}
        replace
      />
    );
  }

  return <>{children}</>;
};
